import { type ArdoqId, GroupType } from '@ardoq/api-types';
import { GraphComponent, GraphItemTypes, INode, Point } from '@ardoq/yfiles';
import type { GraphNode } from '@ardoq/graph';
import { createSvgElement } from '@ardoq/dom-utils';
import { fromEvent, withLatestFrom } from 'rxjs';
import type { HandleImageDropCallback } from 'yfilesExtensions/view/modernized/types';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import {
  type ImageDropTargetContext,
  isValidImageDropTarget,
} from './modernized/imageDropTarget';
import { imageDropTargetContext$ } from './modernized/imageDropTargetContext$';
import { ModernizedHighlightIndicatorManager } from './modernized/ModernizedHighlightIndicatorManager';
import {
  type DropzoneHighlightStateHolder,
  type DropzoneNodeState,
  emptyDropzoneHighlightState,
  withHoveredModelId,
} from './dropzoneHighlightState';

const hitComponentId = (evt: DragEvent, graphComponent: GraphComponent) => {
  const location = graphComponent.viewToWorldCoordinates(
    new Point(evt.offsetX, evt.offsetY)
  );
  const {
    graphModelManager,
    renderTree: { contentGroup },
  } = graphComponent;
  const [hitElement] = graphModelManager.hitElementsAt({
    location,
    root: contentGroup,
    typeFilter: GraphItemTypes.NODE,
  });
  if (!hitElement) {
    return null;
  }
  const { tag } = hitElement;
  const { modelId } = tag as GraphNode;
  return modelId;
};

const isDraggingImage = (evt: DragEvent) => {
  const imageTypes = [
    'image/svg+xml',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/jpeg',
    'image/webp',
  ];
  if (evt.dataTransfer && evt.dataTransfer.items) {
    const fileType = evt.dataTransfer.items[0]?.type;
    if (fileType) return imageTypes.includes(fileType);
    return false;
  }
  return false;
};

const createMask = (clipId: string) => {
  const mask = createSvgElement('g', {
    'clip-path': `url(#${clipId})`,
  });
  const rect = createSvgElement('rect', {
    width: `100%`,
    height: `100%`,
    fill: 'white',
  });
  mask.appendChild(rect);
  return mask;
};

const configureInvalidDropzoneOverlayPanel = (
  overlayPanel: HTMLElement,
  clipId: string
) => {
  const invalidDropzonePattern = createSvgElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    opacity: '0.8',
    style:
      'position:absolute;top:0;left:0;height:100%;width:100%;display:block;pointer-events:none;',
  });
  const clipNode = createSvgElement('clipPath', { id: clipId });
  invalidDropzonePattern.appendChild(createMask(clipId));
  invalidDropzonePattern.appendChild(clipNode);
  overlayPanel.appendChild(invalidDropzonePattern);
  overlayPanel.setAttribute(
    'style',
    'position:absolute;top:0;left:0;height:100%;width:100%;display:block;pointer-events:none;'
  );

  return clipNode;
};

const isDropzoneCandidate = (node: INode): boolean =>
  node.tag !== null &&
  node.tag?.type !== GroupType.COMPONENT &&
  node.tag?.type !== GroupType.FIELD &&
  node.tag?.type !== GroupType.TAG &&
  node.tag?.type !== GroupType.WORKSPACE;

const toDropzoneNodeState = (
  graphComponent: GraphComponent,
  context: ImageDropTargetContext,
  node: INode
): DropzoneNodeState => {
  const modelId = dataModelId(node.tag);
  return {
    modelId,
    isGroupNode: graphComponent.graph.isGroupNode(node),
    isValidDropTarget: isValidImageDropTarget(context, modelId),
  };
};

type InitializeHighlightIndicatorArgs = {
  graphComponent: GraphComponent;
  viewInstanceId: string;
  handleImageDropCallback: HandleImageDropCallback;
};

const initializeHighlightIndicator = ({
  graphComponent,
  viewInstanceId,
  handleImageDropCallback,
}: InitializeHighlightIndicatorArgs): VoidFunction => {
  const dropzoneState: DropzoneHighlightStateHolder = {
    current: emptyDropzoneHighlightState,
  };
  graphComponent.highlightIndicatorManager =
    new ModernizedHighlightIndicatorManager(dropzoneState);

  const addValidDropzoneHighlightToNodes = (
    evt: DragEvent,
    context: ImageDropTargetContext
  ) => {
    evt.preventDefault();

    transitioning = true;
    setTimeout(() => {
      transitioning = false;
    }, 1);

    if (isDraggingImage(evt)) {
      const candidates = graphComponent.graph.nodes
        .filter(isDropzoneCandidate)
        .toArray();
      dropzoneState.current = {
        ...dropzoneState.current,
        nodes: new Map(
          candidates
            .map(node => toDropzoneNodeState(graphComponent, context, node))
            .map((nodeState): [ArdoqId, DropzoneNodeState] => [
              nodeState.modelId,
              nodeState,
            ])
        ),
      };
      const { highlights } = graphComponent;
      candidates.forEach(node => highlights.add(node));
    }
  };

  const setHoveredDropzone = (evt: DragEvent) => {
    evt.preventDefault();

    transitioning = true;
    setTimeout(() => {
      transitioning = false;
    }, 1);

    graphComponent.invalidate();

    dropzoneState.current = withHoveredModelId(
      dropzoneState.current,
      hitComponentId(evt, graphComponent)
    );
  };

  const clearHighlights = (evt: DragEvent) => {
    evt.preventDefault();
    graphComponent.invalidate();

    if (transitioning === false) {
      const { highlights } = graphComponent;
      highlights.clear();
      dropzoneState.current = emptyDropzoneHighlightState;
      clipNode.innerHTML = '';
    }
  };

  const clipNode = configureInvalidDropzoneOverlayPanel(
    graphComponent.overlayPanel,
    `dropzone-clip-${viewInstanceId}`
  );

  let transitioning = false;

  const upsertIcon = (evt: DragEvent) => {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    graphComponent.invalidate();

    const componentId = hitComponentId(evt, graphComponent);
    const file = Array.from(evt.dataTransfer?.files || [])[0];

    if (componentId && file) {
      handleImageDropCallback(componentId, file);
    }
  };

  const dragEnterSubscription = fromEvent<DragEvent>(window, 'dragenter')
    .pipe(withLatestFrom(imageDropTargetContext$))
    .subscribe(([evt, context]) =>
      addValidDropzoneHighlightToNodes(evt, context)
    );
  window.addEventListener('dragover', setHoveredDropzone);
  window.addEventListener('dragleave', clearHighlights);
  window.addEventListener('drop', clearHighlights);
  window.addEventListener('drop', upsertIcon);

  const unregister = () => {
    dragEnterSubscription.unsubscribe();
    window.removeEventListener('dragover', setHoveredDropzone);
    window.removeEventListener('dragleave', clearHighlights);
    window.removeEventListener('drop', clearHighlights);
    window.removeEventListener('drop', upsertIcon);
  };
  return unregister;
};
export { initializeHighlightIndicator };
